import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { MediaQuery } from '@grandvisionhq/common';
import { useIntl } from '@grandvisionhq/state';
import { placeholderImage, createImageUrl } from '@grandvisionhq/utils';
import '../../context/actions.js';
import '../../context/reducer.js';
import { useProductData } from '../../context/product-context-hooks.js';
import '../../context/ProductContext.js';
import '../../context/CmsContext.js';
import { pushImageGalleryItemExpandedEvent } from '../Analytics/events/ImageGalleryEvents.js';
import 'classnames';
import '@grandvisionhq/iris-ui';
import { ProductImageGalleryMobile } from './components/ProductImageGalleryMobile/ProductImageGalleryMobile.js';
import 'react-zoom-pan-pinch';
import '@grandvisionhq/icons';
import '@grandvisionhq/lib-utils-react';
import { ProductImageGallery } from './ProductImageGallery.js';
import { ProductImageGallerySlotsProvider } from './slots/context.js';
import { createAltTag } from './utils/create-alt-tag.js';

const defaultImageParams = {
    fit: 'fill',
    format: 'png',
    aspectRatio: 16 / 9
};
const highlightedPackshots = new Set([
    'On_Model_Female01',
    'On_Model_Female02',
    'On_Model_Female03',
    'On_Model_Male01',
    'On_Model_Male02',
    'On_Model_Male03', 
]);
const SLOTS_DEFAULT = {};
const ProductImageGalleryContainer = ({ slots =SLOTS_DEFAULT  })=>{
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const { getLabel  } = useIntl();
    const [isImageVisible, setImageVisible] = React.useState(false);
    const [zoomMobileOpenIndex, setZoomMobileOpenIndex] = React.useState(null);
    const buttonClickedForZoomRef = React.useRef(null);
    const [isExpanded, setIsExpanded] = React.useState(false);
    const { activeVariant  } = useProductData();
    let isVariantVtoEnabled = false;
    if ('isVtoEnabled' in activeVariant) {
        isVariantVtoEnabled = activeVariant.isVtoEnabled;
    }
    const handleGalleryExpansion = React.useCallback((expanded)=>{
        if (!isImageVisible && expanded) {
            setImageVisible(true);
        }
        setIsExpanded(expanded);
    }, [
        isImageVisible
    ]);
    React.useEffect(()=>{
        if (isImageVisible) {
            pushImageGalleryItemExpandedEvent(isExpanded);
        }
    }, [
        isImageVisible,
        isExpanded
    ]);
    const handleImageInteraction = React.useCallback((index)=>{
        if (index === null) {
            var ref;
            (ref = buttonClickedForZoomRef.current) === null || ref === void 0 ? void 0 : ref.focus();
        }
        setZoomMobileOpenIndex(index);
    }, []);
    const { initialImages: initialImages1 , expandableImages: expandableImages1 , enhancedImages: enhancedImages1  } = React.useMemo(()=>{
        var ref;
        // Use placeholder if there are no images
        const variantImages = ((ref = activeVariant.media.images) === null || ref === void 0 ? void 0 : ref.length) > 0 ? activeVariant.media.images : [
            placeholderImage
        ];
        var _label, _label1, _label2;
        const enhancedImages = variantImages.map((image)=>swcHelpers.objectSpread({}, image, {
                label: (_label = image.label) !== null && _label !== void 0 ? _label : '',
                thumbnailUrl: createImageUrl(image.url, swcHelpers.objectSpread({}, defaultImageParams, {
                    width: 224
                })),
                altText: createAltTag({
                    variant: activeVariant,
                    prefix: image.label
                }),
                highlighted: highlightedPackshots.has((_label1 = image.label) !== null && _label1 !== void 0 ? _label1 : ''),
                zoom: !highlightedPackshots.has((_label2 = image.label) !== null && _label2 !== void 0 ? _label2 : '')
            })
        );
        /**
     * In order to correctly follow the design we need to decide how many images we will initially render on load.
     * If there is a highlightedPackshot we have more space to render an extra image.
     *
     * This follows the following logic:
     * 1. If there are no highlightedPackshots: render 3 images.
     * 2. If only the first image is a highlightedPackshot: render 3 images.
     * 3. If the second OR third image is a highlightedPackshot: render 4 images.
     * 4. If the second AND third images are highlightedPackshots: render 3 images.
     */ const initialImageSize = enhancedImages.slice(1, 3).filter((image)=>image.highlighted === true
        ).length === 1 ? 4 : 3;
        const initialImages = enhancedImages.slice(0, initialImageSize);
        const expandableImages = enhancedImages.slice(initialImageSize);
        return {
            initialImages,
            expandableImages,
            enhancedImages
        };
    }, [
        activeVariant
    ]);
    return /*#__PURE__*/ React.createElement(ProductImageGallerySlotsProvider, {
        slots: slots
    }, isSmallScreen ? /*#__PURE__*/ React.createElement(ProductImageGalleryMobile, {
        images: enhancedImages1,
        sku: activeVariant.sku,
        imageParams: defaultImageParams,
        handleImageInteraction: handleImageInteraction,
        zoomModalOpenIndex: zoomMobileOpenIndex,
        hasTopSpacing: isVariantVtoEnabled
    }) : /*#__PURE__*/ React.createElement(ProductImageGallery, {
        initialImages: initialImages1,
        expandableImages: expandableImages1,
        showMoreLabel: getLabel('products.imageGallery.showMore'),
        showLessLabel: getLabel('products.imageGallery.showLess'),
        isImageVisible: isImageVisible,
        isExpanded: isExpanded,
        handleGalleryExpansion: handleGalleryExpansion,
        handleImageInteraction: handleImageInteraction,
        sku: activeVariant.sku,
        zoomModalOpenIndex: zoomMobileOpenIndex,
        buttonClickedForZoomRef: buttonClickedForZoomRef
    }));
};

export { ProductImageGalleryContainer };
//# sourceMappingURL=ProductImageGalleryContainer.js.map
